import React, { createContext, useContext, useMemo } from 'react'

import { useKnowledgeDocumentDataContext } from './KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from './KnowledgeDocumentUXContext'

export interface DocumentAgentState {
  siteId: string
  orgId: string
  openDocumentIds: string[]
  openTimeSeriesSensorIds: string[]
  activeTabType: 'search' | 'document' | 'timeseries'
  activeDocumentId: string | null
  activeTimeSeriesSensorIds: string[]
}

interface DocumentAgentContextType {
  agentState: DocumentAgentState
}

const DocumentAgentContext = createContext<
  DocumentAgentContextType | undefined
>(undefined)

export const DocumentAgentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { siteID, orgID } = useKnowledgeDocumentDataContext()
  const {
    openTabs,
    isOnDocument,
    isOnTimeSeries,
    currentDocumentId,
    currentSensorIds,
  } = useKnowledgeDocumentUXContext()

  const agentState = useMemo<DocumentAgentState>(() => {
    const openDocumentIds: string[] = []
    const openTimeSeriesSensorIds: string[] = []

    for (const tab of openTabs) {
      const currentHistoryItem = tab.history[tab.currentIndex]
      if (currentHistoryItem.type === 'document') {
        openDocumentIds.push(currentHistoryItem.documentId)
      } else if (currentHistoryItem.type === 'timeseries') {
        openTimeSeriesSensorIds.push(...currentHistoryItem.sensorIds)
      }
    }

    const uniqueDocumentIds = [...new Set(openDocumentIds)]
    const uniqueSensorIds = [...new Set(openTimeSeriesSensorIds)]

    let activeTabType: 'search' | 'document' | 'timeseries' = 'search'
    if (isOnDocument) {
      activeTabType = 'document'
    } else if (isOnTimeSeries) {
      activeTabType = 'timeseries'
    }

    return {
      siteId: siteID,
      orgId: orgID,
      openDocumentIds: uniqueDocumentIds,
      openTimeSeriesSensorIds: uniqueSensorIds,
      activeTabType,
      activeDocumentId: isOnDocument ? currentDocumentId : null,
      activeTimeSeriesSensorIds: isOnTimeSeries ? currentSensorIds : [],
    }
  }, [
    siteID,
    orgID,
    openTabs,
    isOnDocument,
    isOnTimeSeries,
    currentDocumentId,
    currentSensorIds,
  ])

  const value = useMemo(() => ({ agentState }), [agentState])

  return (
    <DocumentAgentContext.Provider value={value}>
      {children}
    </DocumentAgentContext.Provider>
  )
}

export const useDocumentAgentContext = (): DocumentAgentContextType => {
  const context = useContext(DocumentAgentContext)
  if (context === undefined) {
    throw new Error(
      'useDocumentAgentContext must be used within a DocumentAgentProvider'
    )
  }
  return context
}

export const useDocumentAgentState = (): DocumentAgentState => {
  const { agentState } = useDocumentAgentContext()
  return agentState
}

/**
 * Returns the document agent state if inside a DocumentAgentProvider, otherwise undefined.
 * Use this when the chatbot may or may not be rendered in a document viewer context.
 */
export const useDocumentAgentStateOptional = ():
  | DocumentAgentState
  | undefined => {
  const context = useContext(DocumentAgentContext)
  return context?.agentState
}
